<template>
  <div class="dialogs-list">
    <article
      v-for="dialog in dialogs"
      :key="dialog.id"
      :class="['dialogs-list-item', { 'is-active': isSelected(dialog) }]"
      @click="$emit('select-dialog', dialog)"
    >
      <div class="dialogs-list-item__main">
        <el-avatar
          :size="54"
          :src="getAvatar(dialog)"
          class="dialogs-list-item__avatar"
        >
          <template v-if="!getAvatar(dialog)">{{ clientName(dialog.client_name) }}</template>
        </el-avatar>

        <div class="dialogs-list-item__body">
          <div class="dialogs-list-item__head">
            <div class="dialogs-list-item__title">
              <span class="dialogs-list-item__name">{{ dialog.client_name || 'Без имени' }}</span>
              <span
                v-if="dialog.messenger_type"
                :class="['dialogs-list-item__messenger', `dialogs-list-item__messenger--${dialog.messenger_type}`]"
              >
                <font-awesome-icon :icon="getMessengerIcon(dialog.messenger_type)" />
              </span>
            </div>
            <span class="dialogs-list-item__time">{{ formatLastMessageDate(dialog.last_active) }}</span>
          </div>

          <div class="dialogs-list-item__footer">
            <p class="dialogs-list-item__preview">{{ previewText(dialog) }}</p>
            <span v-if="Number(dialog.unread_count || 0) > 0" class="dialogs-list-item__badge">
              {{ dialog.unread_count }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="isSelected(dialog)" class="dialogs-list-item__actions" aria-hidden="true">
        <span class="dialogs-list-action dialogs-list-action--archive">
          <el-icon><Download /></el-icon>
          <small>Архив</small>
        </span>
        <span class="dialogs-list-action dialogs-list-action--delete">
          <el-icon><Delete /></el-icon>
          <small>Удалить</small>
        </span>
      </div>
    </article>

    <div v-if="!dialogs.length" class="dialogs-list-empty">Диалоги не найдены</div>
  </div>
</template>

<script>
import { Delete, Download } from '@element-plus/icons-vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'DialogsList',
  components: {
    Delete,
    Download,
    FontAwesomeIcon
  },
  props: {
    dialogs: {
      type: Array,
      default: () => []
    },
    selectedDialogId: {
      type: [Number, String],
      default: null
    }
  },
  methods: {
    isSelected(dialog) {
      return String(this.selectedDialogId) === String(dialog.id)
    },
    clientName(name) {
      if (!name) return ''
      const parts = name.trim().split(' ').filter(Boolean)
      if (parts.length === 0) return ''
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase()
      }
      return (parts[0][0] + parts[1][0]).toUpperCase()
    },
    getMessengerIcon(type) {
      switch (type) {
        case 'whatsapp':
          return ['fab', 'whatsapp']
        case 'telegram':
          return ['fab', 'telegram']
        case 'vk':
          return ['fab', 'vk']
        case 'email':
          return ['far', 'envelope']
        default:
          return ['far', 'circle-question']
      }
    },
    getAvatar(dialog) {
      if (dialog.client_avatar) return dialog.client_avatar
      if (dialog.client_sex === 1) return '/img/avatar-male.svg'
      if (dialog.client_sex === 2) return '/img/avatar-female.svg'
      return null
    },
    previewText(dialog) {
      return dialog.last_message_preview || dialog.last_message || dialog.last_message_text || dialog.client_phone || 'Диалог без сообщений'
    },
    formatLastMessageDate(date) {
      if (!date) return ''
      const parsed = new Date(String(date).replace(' ', 'T'))
      if (Number.isNaN(parsed.getTime())) return ''

      const now = new Date()
      const today = parsed.toDateString() === now.toDateString()
      const yesterday = new Date(now)
      yesterday.setDate(now.getDate() - 1)
      const isYesterday = parsed.toDateString() === yesterday.toDateString()

      if (today) {
        return parsed.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      }
      if (isYesterday) {
        return 'Вчера'
      }

      const diffDays = Math.floor((now - parsed) / 86400000)
      if (diffDays < 7) {
        const weekday = parsed.toLocaleDateString('ru-RU', { weekday: 'short' })
        return weekday.slice(0, 1).toUpperCase() + weekday.slice(1, 2)
      }

      return parsed.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
    }
  }
}
</script>

<style scoped>
.dialogs-list {
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
}

.dialogs-list-item {
  overflow: hidden;
  border-radius: 12px;
  background: linear-gradient(180deg, #133264 0%, #0f2751 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.5);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.dialogs-list-item:hover {
  transform: translateY(-1px);
  box-shadow: inset 0 0 0 1px rgba(69, 119, 198, 0.72), 0 10px 24px rgba(4, 14, 36, 0.18);
}

.dialogs-list-item__main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 86px;
  padding: 12px 14px;
}

.dialogs-list-item__avatar {
  flex-shrink: 0;
  background: #e8edf4;
  color: #45607f;
  font-family: "Oswald", sans-serif;
  font-size: 18px;
}

.dialogs-list-item__body {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dialogs-list-item__head,
.dialogs-list-item__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.dialogs-list-item__title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dialogs-list-item__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
  font-family: "Oswald", sans-serif;
  font-size: 18px;
  line-height: 1;
}

.dialogs-list-item__messenger {
  flex-shrink: 0;
  font-size: 15px;
}

.dialogs-list-item__messenger--telegram {
  color: #27a9ff;
}

.dialogs-list-item__messenger--whatsapp {
  color: #35dc86;
}

.dialogs-list-item__messenger--vk {
  color: #4ea2ff;
}

.dialogs-list-item__messenger--email {
  color: #8dc6ff;
}

.dialogs-list-item__time {
  flex-shrink: 0;
  color: #ffffff;
  font-family: "Oswald", sans-serif;
  font-size: 16px;
}

.dialogs-list-item__preview {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #00c3fe;
  font-family: "Oswald", sans-serif;
  font-size: 15px;
}

.dialogs-list-item__badge {
  flex-shrink: 0;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #00aef0;
  color: #07162f;
  font-family: "Oswald", sans-serif;
  font-size: 14px;
  line-height: 1;
}

.dialogs-list-item.is-active {
  background: linear-gradient(180deg, #184082 0%, #123261 100%);
  box-shadow: inset 0 0 0 1px rgba(69, 119, 198, 0.95);
}

.dialogs-list-item.is-active .dialogs-list-item__preview {
  color: #d7e6ff;
}

.dialogs-list-item__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid rgba(69, 119, 198, 0.55);
}

.dialogs-list-action {
  min-height: 58px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #ffffff;
  font-family: "Oswald", sans-serif;
}

.dialogs-list-action .el-icon {
  font-size: 21px;
}

.dialogs-list-action--archive {
  background: linear-gradient(180deg, #4477c8 0%, #2f5ea9 100%);
}

.dialogs-list-action--delete {
  background: linear-gradient(180deg, #d6201f 0%, #b90f0f 100%);
}

.dialogs-list-empty {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(180deg, #133264 0%, #0f2751 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.5);
  color: #b9d5ff;
  font-family: "Oswald", sans-serif;
  font-size: 18px;
}

@media (max-width: 900px) {
  .dialogs-list-item__main {
    min-height: 80px;
  }
}

@media (max-width: 540px) {
  .dialogs-list-item__main {
    padding: 12px;
  }

  .dialogs-list-item__name {
    font-size: 17px;
  }

  .dialogs-list-item__preview,
  .dialogs-list-item__time {
    font-size: 14px;
  }

  .dialogs-list-item__actions {
    display: none;
  }
}
</style>
